import { getLabelFromStorage, consoleEvents } from '@pb/utils'
import { mergeDeepRight } from 'ramda'
import { batch } from 'redux-act'

import {
  CHECKOUT_TRACKING_SECTIONS,
  SHOW_NOTIFICATION_TIME,
  TECHNICAL_ERROR_LABELS,
  ADDRESSES_TYPES,
  CHECKOUT_TRACKING_ERROR_TYPES,
  ADDRESS_NOT_VALIDATED,
  ADDRESS_SECTION_STATES,
  ADDRESS_VALIDATED,
} from 'features/checkout/constants'
import {
  hideAddressSuggestions,
  setAddressSuggestions,
  updateAddressFail,
  updateAddressStart,
  updateAddressSuccess,
} from 'features/checkout/duck/actions'
import {
  normalizeAddressOperationResponseV2,
  normalizeContextPartial,
} from 'features/checkout/duck/normalize'
import checkoutSelectors from 'features/checkout/duck/selectors'
import { updateAddress, postNewAddress, addressValidate } from 'features/checkout/requests'
import { AddressResponseData, DeliveryAddressFormData } from 'features/checkout/types'
import { showNotification } from 'features/notifications/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { ThunkResult } from 'redux/types'

import { trackError } from '../track'
import { getMarketCode } from 'App/duck/selectors'
import { PostAddressType, UpdateAddressType } from './types'

const postAddressesForCustomer = ({
  addressDetails,
  userHasDeliveryAddress,
  currentCountry,
}: PostAddressType): Promise<AddressResponseData> => {
  const isBillingAddressWithSameCountry =
    !addressDetails.country || addressDetails.country === currentCountry

  if (isBillingAddressWithSameCountry && !userHasDeliveryAddress) {
    return postNewAddress({ ...addressDetails, type: ADDRESSES_TYPES.billingAndDelivery })
  }
  return postNewAddress({ ...addressDetails, type: ADDRESSES_TYPES.billing })
}

const updateAddressesForCustomer = async ({
  addressDetails,
  addressId,
  userHasDeliveryAddress,
  currentCountry,
}: UpdateAddressType): Promise<AddressResponseData> => {
  const updateAddressResponse = await updateAddress(addressId, {
    ...addressDetails,
    id: addressId,
  } as DeliveryAddressFormData)

  const isBillingAddressWithSameCountry =
    !addressDetails.country || addressDetails.country === currentCountry

  if (isBillingAddressWithSameCountry && !userHasDeliveryAddress) {
    const postDeliveryResponse = await postNewAddress({
      ...addressDetails,
      type: ADDRESSES_TYPES.delivery,
    })
    const combinedAddressResponse = mergeDeepRight(
      updateAddressResponse,
      postDeliveryResponse,
    ) as AddressResponseData
    return combinedAddressResponse
  }
  return updateAddressResponse
}

export const operationSaveBillingAddress =
  (addressDetails, showSection?: (section: string) => void): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(updateAddressStart())
    try {
      const addressId = checkoutSelectors.getCurrentBillingAddress(getState()).id
      const currentCountry = getMarketCode(getState())
      const isExternalAddress = addressDetails.country && currentCountry !== addressDetails.country
      const homeDeliveryAddresses = checkoutSelectors.getCurrentDeliveryAddresses(getState())
      const userHasDeliveryAddress = homeDeliveryAddresses?.length > 0
      let addressPropsFromValidate = {}
      if (!addressDetails.alreadyValidated && !isExternalAddress && showSection) {
        try {
          const validateAddressResponse = await addressValidate(addressDetails)
          if (validateAddressResponse.decision === ADDRESS_NOT_VALIDATED) {
            const addressSuggestions = normalizeAddressOperationResponseV2(validateAddressResponse)
            if (addressSuggestions) {
              dispatch(setAddressSuggestions(addressSuggestions))
              showSection(ADDRESS_SECTION_STATES.validateBilling)
              dispatch(updateAddressFail([]))
              return
            }
          }
          // TODO: Replace full address object from address_validate response once id is sent from BE [COWEB-2907]
          else if (validateAddressResponse.decision === ADDRESS_VALIDATED) {
            const validatedADAddress = validateAddressResponse?.addressSuggestions?.[0]
            const isPoBox = validatedADAddress?.isPoBox
            addressPropsFromValidate = {
              ...addressPropsFromValidate,
              ...(typeof isPoBox !== 'undefined' && { isPoBox }),
            }
          }
        } catch (error) {
          consoleEvents.error('Failed to validate address', error)
        }
      }
      dispatch(hideAddressSuggestions())
      const requestFn = (): Promise<AddressResponseData> =>
        addressId
          ? updateAddressesForCustomer({
              addressDetails: { ...addressDetails, ...addressPropsFromValidate },
              addressId,
              userHasDeliveryAddress,
              currentCountry,
            })
          : postAddressesForCustomer({
              addressDetails: { ...addressDetails, ...addressPropsFromValidate },
              userHasDeliveryAddress,
              currentCountry,
            })

      const addressResponse = await requestFn()
      const normalizedContext = dispatch(normalizeContextPartial(addressResponse))
      dispatch(updateAddressSuccess(normalizedContext))
    } catch (error) {
      consoleEvents.error('Failed to save billing address', error)
      const { isClientError, isTechnicalError, code } = getErrorData(error)
      const errorMessage = getLabelFromStorage(TECHNICAL_ERROR_LABELS.updateAddress)
      if (isClientError) {
        dispatch(updateAddressFail(error.response.data))
      }
      if (isTechnicalError) {
        dispatch(
          batch(
            updateAddressFail([]),
            hideAddressSuggestions(),
            showNotification({
              content: errorMessage,
              type: 'error',
              timespan: SHOW_NOTIFICATION_TIME,
            }),
          ),
        )
      }
      dispatch(
        trackError({
          section: CHECKOUT_TRACKING_SECTIONS.deliveryLocation,
          message: errorMessage,
          id: code,
          errorType: CHECKOUT_TRACKING_ERROR_TYPES.system,
        }),
      )
    }
  }
